import _ from "lodash";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Box, Collapse, InputAdornment, List, ListItemButton, Stack, TextField} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {Company} from "../../types/domain/company";
import {containsSubstringCaseInsensitive} from "../../utils/stringUtils";
import {DEMO_COMPANY_ID} from "../../utils/constants";

export type RenderCompanyItemFn = (props: {
    company: Company;
    selectedCompanyId: string | null;
    searchFilter: string;
}) => React.ReactNode;

export interface Props {
    companies: Company[];
    isLoading: boolean;
    selectedCompanyId: string | null;
    onCompanySelect?: (companyId: string) => void;
    renderItem: RenderCompanyItemFn;
}

export const CompanyHierarchyView: React.FC<Props> = (props) => {
    const {t} = useTranslation();
    const {companies, isLoading, selectedCompanyId, onCompanySelect, renderItem} = props;
    const [searchFilter, setSearchFilter] = useState<string>("");
    const [companiesTree, setCompaniesTree] = useState<CompanyNode[]>([]);

    useEffect(() => {
        if (!isLoading) {
            setCompaniesTree(transformFlatArrayToTree(_.cloneDeep(companies as CompanyNode[])));
        }
    }, [isLoading, companies, setCompaniesTree]);

    return (
        <Stack direction="column" spacing="1rem" overflow="hidden">
            <TextField
                InputProps={{
                    disableUnderline: true,
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                    ),
                }}
                hiddenLabel
                margin="none"
                size="small"
                id="searchCompany"
                data-test="searchCompany"
                type="search"
                fullWidth
                variant="filled"
                placeholder={t("companies.hierarchy_view.input_placeholder")}
                value={searchFilter}
                onChange={(event) => setSearchFilter(event.target.value)}
            />
            {!isLoading && (
                <Box sx={{overflowY: "auto"}}>
                    <List sx={{margin: 0, padding: 0}}>
                        {companiesTree.map((company) =>
                            renderTree({
                                company,
                                level: 0,
                                selectedCompanyId,
                                onCompanySelect,
                                searchFilter: _.trim(searchFilter),
                                renderItem,
                            }),
                        )}
                    </List>
                </Box>
            )}
        </Stack>
    );
};

/**
 * A tree node to construct a tree structure for Companies
 */
interface CompanyNode extends Company {
    children: CompanyNode[];
}

/**
 * Recursively renders Companies tree
 */
const renderTree = (props: {
    company: CompanyNode;
    level: number;
    selectedCompanyId: string | null;
    onCompanySelect?: (companyId: string) => void;
    searchFilter: string;
    renderItem: RenderCompanyItemFn;
}) => {
    const {company, level, selectedCompanyId, onCompanySelect, searchFilter, renderItem} = props;
    const children = Array.isArray(company.children)
        ? company.children.map((node) =>
              renderTree({
                  company: node,
                  level: level + 1,
                  selectedCompanyId,
                  onCompanySelect,
                  searchFilter,
                  renderItem,
              }),
          )
        : null;
    const hasEmptyChildren = children === null || children.every((item) => item === null);

    if (hasEmptyChildren && !containsSubstringCaseInsensitive(company.properties.name, searchFilter)) {
        return null;
    } else {
        return (
            <div key={company.id}>
                <ListItemButton
                    sx={{
                        pl: level * 3 + 1.5,
                        height: "36px",
                        maxHeight: "36px",
                        "&.Mui-selected, &.Mui-selected:hover": {
                            backgroundColor: "#5161C1",
                            color: "white",
                        },
                    }}
                    disableRipple
                    selected={company.id === selectedCompanyId}
                    onClick={() => onCompanySelect && onCompanySelect(company.id)}
                >
                    {renderItem({company, selectedCompanyId, searchFilter})}
                </ListItemButton>
                <Collapse in={true} unmountOnExit>
                    <List component="div" disablePadding>
                        {children}
                    </List>
                </Collapse>
            </div>
        );
    }
};

/**
 * Transforms flat array of companies (retrieved form the backend) to a tree structure
 */
const transformFlatArrayToTree = (companies: CompanyNode[]): CompanyNode[] => {
    const nodes: Record<string, CompanyNode> = {};
    const orderedCompanies = _.orderBy(companies, (company) => company.parentCompaniesIds.length);

    return orderedCompanies
        .filter((c) => {
            const id = c.id;
            const parentId = c.parentCompanyId;

            nodes[id] = _.defaults(c, nodes[id], {children: []});

            if (parentId && parentId in nodes) {
                nodes[parentId].children.push(c);
                return false;
            } else {
                return true;
            }
        })
        .sort(companiesSortFn);
};

const companiesSortFn = (left: CompanyNode, right: CompanyNode) => {
    if (left.id === DEMO_COMPANY_ID) {
        return 1;
    } else if (right.id === DEMO_COMPANY_ID) {
        return -1;
    } else {
        return left.properties.name.localeCompare(right.properties.name, undefined, {caseFirst: "lower"});
    }
};
